import {
  DndContext,
  DragOverlay,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { CardPrazo } from '../components/CardPrazo';
import { ColunaKanban } from '../components/ColunaKanban';
import { EsteiraAndamento } from '../components/EsteiraAndamento';
import { CriarPrazoDialog } from '../components/CriarPrazoDialog';
import { Legenda } from '../components/Legenda';
import { ModalDetalhe } from '../components/ModalDetalhe';
import { BuscaProcesso } from '../components/BuscaProcesso';
import { PageShell } from '../components/PageShell';
import { ehAdmin, useMe, usePrazosSemana, markCardAsMoved } from '../hooks';
import type {
  ColunaKanban as ColunaKanbanType,
  Etiqueta,
  EndpointTransicao,
  NomeTransicao,
  PrazoCard,
  TransicoesPermitidas,
} from '../types';
import {
  semanaAdjacente,
  semanaISO,
  semanaToDatas,
  transicaoParaColuna,
} from '../utils';

const COLUNAS_ALVO: ColunaKanbanType[] = [
  'nao_feitos', 'em_andamento', 'pronto_protocolo', 'concluidos',
];


function obterCorTemperatura(t: number): string {
  const ratio = (t - 0.1) / 9.9; // 0 to 1
  const r = Math.round(59 + (239 - 59) * ratio);
  const g = Math.round(130 + (68 - 130) * ratio);
  const b = Math.round(246 + (68 - 246) * ratio);
  return `rgb(${r}, ${g}, ${b})`;
}

export function KanbanSemanal() {
  const [semana, setSemana] = useState<string>(semanaISO());
  const [detalheId, setDetalheId] = useState<number | null>(null);
  const [dragCard, setDragCard] = useState<PrazoCard | null>(null);
  const [erroDrop, setErroDrop] = useState<string | null>(null);
  const [criarOpen, setCriarOpen] = useState(false);
  const [aba, setAba] = useState<'producao' | 'pronto_protocolo' | 'concluidos'>('producao');
  // Oryzo: Privacy Flip Mode (persiste)
  const [privacyOn, setPrivacyOn] = useState<boolean>(
    () => localStorage.getItem('privacyMode') === '1',
  );
  const { data, isLoading, error } = usePrazosSemana(semana);
  const { data: me } = useMe();
  const queryClient = useQueryClient();

  // SLA T automatic calculation based on all active deadlines of the week
  const cards = data?.colunas.flatMap((c) => c.cards) ?? [];
  const activeCards = cards.filter((c) => c.etiqueta !== 'cinza');
  const redCount = activeCards.filter((c) => c.dias_ate_vencimento <= 3).length;
  const yellowCount = activeCards.filter((c) => c.dias_ate_vencimento > 3 && c.dias_ate_vencimento < 8).length;
  const whiteCount = activeCards.filter((c) => c.dias_ate_vencimento >= 8).length;
  const totalActive = redCount + yellowCount + whiteCount;
  const temperatura = totalActive > 0 ? Math.max(0.1, (redCount * 10 + yellowCount * 5) / totalActive) : 1.0;

  // Configuração inicial do quadro dependendo do papel
  useEffect(() => {
    if (me?.papel === 'colaborador') {
      setAba('pronto_protocolo');
    } else if (me?.papel === 'correspondente') {
      setAba('concluidos');
    }
    if (me?.papel === 'testador') {
      setPrivacyOn(true);
    }
  }, [me?.papel]);

  /**
   * Abertura do painel lateral gated por papel + coluna:
   *  - NUNCA abre para cards da TRIAGEM (coluna 'nao_feitos') — lá o usuário só
   *    Assume/Exclui pelos botões; o detalhe (com upload do PDF) é em Em Andamento.
   *  - testador/audiencista não abrem.
   */
  function abrirDetalhe(id: number) {
    if (me?.papel === 'testador' || me?.papel === 'audiencista') return;
    const card = data?.colunas.flatMap((c) => c.cards).find((c) => c.id_prazo === id);
    if (card?.coluna === 'nao_feitos') return;   // Triagem: clique no card não abre nada
    setDetalheId(id);
  }

  /** Busca: pula para a SEMANA do processo e abre o detalhe (no contexto do quadro). */
  function irParaProcesso(card: PrazoCard) {
    const iso = card.data_evento ?? card.data_vencimento;
    setSemana(semanaISO(new Date(iso + 'T00:00:00')));
    if (me?.papel === 'testador' || me?.papel === 'audiencista') return;
    setDetalheId(card.id_prazo);
  }

  // Drag-and-drop entre colunas DESATIVADO (decisão 2026-06-10): transições
  // só pelos botões do card/modal. useSensors() vazio = DndContext inerte,
  // clique continua abrindo o modal normalmente. Para reativar, restaurar:
  //   useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))
  const sensors = useSensors();

  const [seg, dom] = semanaToDatas(semana);

  // ─── Drag-and-drop handlers (Fase 8) ─────────────────────────────────
  function transicoesDoCache(idPrazo: number): NomeTransicao[] {
    const tp = queryClient.getQueryData<TransicoesPermitidas>(['transicoes', idPrazo]);
    return tp?.transicoes ?? [];
  }

  function onDragStart(evt: DragStartEvent) {
    setErroDrop(null);
    const idPrazo = evt.active.data.current?.idPrazo as number | undefined;
    if (!idPrazo || !data) { setDragCard(null); return; }
    const card = data.colunas
      .flatMap((c) => c.cards)
      .find((c) => c.id_prazo === idPrazo);
    setDragCard(card ?? null);
  }

  async function onDragEnd(evt: DragEndEvent) {
    setDragCard(null);
    const dest = evt.over?.id;
    const idPrazo = evt.active.data.current?.idPrazo as number | undefined;
    const etiqueta = evt.active.data.current?.etiqueta as Etiqueta | undefined;
    if (!dest || !idPrazo || !etiqueta) return;

    const destStr = String(dest);

    if (destStr.startsWith('lane-')) {
      const targetLane = destStr.replace('lane-', '');
      const card = data?.colunas.flatMap(c => c.cards).find(c => c.id_prazo === idPrazo);
      if (!card) return;

      if (card.coluna === 'nao_feitos') {
        const endpoint = transicaoParaColuna(etiqueta, 'em_andamento', transicoesDoCache(idPrazo));
        if (!endpoint) {
          setErroDrop('Transição não permitida.');
          window.setTimeout(() => setErroDrop(null), 4000);
          return;
        }
        try {
          await api.transicao(idPrazo, endpoint);
          markCardAsMoved(idPrazo);
          queryClient.invalidateQueries({ queryKey: ['prazos'] });
          queryClient.invalidateQueries({ queryKey: ['prazo', idPrazo] });
          queryClient.invalidateQueries({ queryKey: ['historico', idPrazo] });
          queryClient.invalidateQueries({ queryKey: ['transicoes', idPrazo] });
          queryClient.invalidateQueries({ queryKey: ['kanban', 'status'] });
        } catch (e: any) {
          setErroDrop(e?.message ?? 'Erro ao assumir');
          window.setTimeout(() => setErroDrop(null), 5000);
        }
      } else if (card.coluna === 'em_andamento') {
        const currentEtapa = card.etapa_pipeline ?? 'fila';
        
        let validaOk = false;
        if (currentEtapa === 'preparo' && targetLane === 'fila') validaOk = true;
        else if (currentEtapa === 'mapa' && targetLane === 'preparo') validaOk = true;
        else if (['mapa_aprovado', 'redigindo', 'revisao', 'concluido'].includes(currentEtapa) && targetLane === 'mapa') validaOk = true;

        if (!validaOk) {
          setErroDrop('Regressão inválida (apenas 1 etapa por vez).');
          window.setTimeout(() => setErroDrop(null), 4000);
          return;
        }

        try {
          await api.setEtapa(idPrazo, targetLane);
          markCardAsMoved(idPrazo);
          queryClient.invalidateQueries({ queryKey: ['prazos'] });
          queryClient.invalidateQueries({ queryKey: ['pecas', idPrazo] });
        } catch (e: any) {
          setErroDrop(e?.message ?? 'Erro ao mover etapa');
          window.setTimeout(() => setErroDrop(null), 5000);
        }
      }
      return;
    }

    const alvo = destStr.replace('col-', '') as ColunaKanbanType;
    if (!COLUNAS_ALVO.includes(alvo)) return;

    const card = data?.colunas.flatMap(c => c.cards).find(c => c.id_prazo === idPrazo);
    if (card && card.coluna === alvo) return;

    const endpoint = transicaoParaColuna(etiqueta, alvo, transicoesDoCache(idPrazo));
    if (!endpoint) {
      setErroDrop(
        `Não posso mover de ${etiqueta} para ${alvo} (sem transição permitida).`,
      );
      window.setTimeout(() => setErroDrop(null), 4000);
      return;
    }

    try {
      await api.transicao(idPrazo, endpoint);
      markCardAsMoved(idPrazo);
      // Invalida queries pra UI refletir o novo estado (igual ao useTransicao)
      queryClient.invalidateQueries({ queryKey: ['prazos'] });
      queryClient.invalidateQueries({ queryKey: ['prazo', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['historico', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['transicoes', idPrazo] });
      queryClient.invalidateQueries({ queryKey: ['kanban', 'status'] });
    } catch (e: any) {
      const status = e?.status;
      setErroDrop(
        status === 403 ? 'Sem permissão pra essa transição.'
        : status === 409 ? 'Alguém alterou o card antes de você.'
        : status === 400 ? 'Transição inválida.'
        : `Erro: ${e?.message ?? 'desconhecido'}`,
      );
      window.setTimeout(() => setErroDrop(null), 5000);
    }
  }

  // Pré-calcula compatibilidade de cada coluna alvo (highlight durante drag)
  function compatColuna(alvo: ColunaKanbanType): 'ok' | 'bloqueado' | 'neutro' {
    if (!dragCard) return 'neutro';
    const ep = transicaoParaColuna(
      dragCard.etiqueta, alvo, transicoesDoCache(dragCard.id_prazo),
    );
    if (ep) return 'ok';
    // Mesma coluna do card → neutro (não é "bloqueio")
    const colunaAtual = data?.colunas.find(
      (c) => c.cards.some((card) => card.id_prazo === dragCard.id_prazo),
    )?.coluna;
    return colunaAtual === alvo ? 'neutro' : 'bloqueado';
  }

  // Lista os 7 dias da semana em ISO
  const dias: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(seg);
    d.setUTCDate(seg.getUTCDate() + i);
    dias.push(d.toISOString().slice(0, 10));
  }

  function togglePrivacy() {
    setPrivacyOn((v) => {
      const nv = !v;
      localStorage.setItem('privacyMode', nv ? '1' : '0');
      return nv;
    });
  }

  // A busca por número agora é transversal (acha em qualquer semana e pula até lá),
  // via <BuscaProcesso> — não filtra mais a semana corrente.
  const dataView = data;
  const tempLabel = temperatura > 7 ? 'crítico' : temperatura < 0.5 ? 'frio' : 'equilibrado';

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
    <PageShell
      className={`${privacyOn ? 'privacy-on' : ''} ${temperatura > 7 ? 'sla-hot' : ''} ${me?.papel === 'testador' ? 'testador-privacy' : ''}`}
      abaixoDaSubbarra={<Legenda />}
      subbarra={
        <>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSemana(semanaAdjacente(semana, -1))}
              className="px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-300"
            >
              ‹
            </button>
            <button
              onClick={() => setSemana(semanaISO())}
              className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 text-blue-700 dark:text-blue-300 rounded text-sm font-medium"
            >
              Hoje
            </button>
            <button
              onClick={() => setSemana(semanaAdjacente(semana, 1))}
              className="px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-300"
            >
              ›
            </button>
            <span className="ml-2 text-sm font-medium">
              {seg.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} —{' '}
              {dom.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <span className="ml-1 text-xs text-gray-400">({semana})</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Busca transversal: acha o processo em qualquer semana e pula até lá */}
            <BuscaProcesso onSelect={irParaProcesso} dica="Clique para ir até a semana do prazo" />

            {/* Oryzo · Temperatura de SLA — simplesmente mostra a temperatura para a semana */}
            <div
              className="hidden sm:flex items-center gap-2 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40"
              title="Temperatura de SLA calculada para a semana"
            >
              <span className="text-[10px] font-mono text-gray-500 dark:text-gray-400 flex items-center gap-0.5">
                SLA T:{' '}
                <strong style={{ color: obterCorTemperatura(temperatura) }}>
                  {temperatura.toFixed(1)}
                </strong>
              </span>
              <input
                type="range" min={0.1} max={10} step={0.1} value={temperatura}
                readOnly
                className="w-24 sla-t-input pointer-events-none"
                style={{
                  color: obterCorTemperatura(temperatura),
                  ['--thumb-color' as any]: obterCorTemperatura(temperatura)
                }}
                aria-label="Temperatura de SLA"
              />
              <span className="text-[9px] uppercase tracking-wide text-gray-400 w-14">{tempLabel}</span>
            </div>

            {/* Oryzo · Privacy Flip Mode */}
            {me?.papel !== 'testador' && (
              <button
                onClick={togglePrivacy}
                title={privacyOn ? 'Modo privado ATIVO — clique para mostrar dados' : 'Ativar modo privado (ofusca CNJ e nomes)'}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border transition-colors ${
                  privacyOn
                    ? 'border-amber-400 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span>{privacyOn ? '🔒' : '🔓'}</span>
                <span className="hidden md:inline">{privacyOn ? 'Privado' : 'Privacidade'}</span>
              </button>
            )}

            <div className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
              {data ? `${data.total} cards na semana` : isLoading ? 'carregando…' : ''}
            </div>
          </div>
        </>
      }
    >
        {error && (
          <div className="text-red-600 dark:text-red-400 p-4 bg-red-50 dark:bg-red-900/30 rounded">
            Erro ao carregar prazos: {String(error)}
          </div>
        )}
        {dataView && (() => {
          const colDe = (nome: ColunaKanbanType) =>
            dataView.colunas.find((c) => c.coluna === nome);
          const colNF = colDe('nao_feitos');
          const colEA = colDe('em_andamento');
          const colPP = colDe('pronto_protocolo');
          const colCC = colDe('concluidos');
          const nCC = colCC?.total ?? 0;
          const nPP = colPP?.total ?? 0;

          return (
            <>
              {/* Abas: Produção (esteira) · Prontos para protocolo · Concluídos (arquivo) */}
              {me?.papel !== 'correspondente' && (
                <div className="flex items-center gap-1 mb-3">
                  <button
                    onClick={() => setCriarOpen(true)}
                    title="Incluir novo prazo (upload dos autos)"
                    className="px-3 py-1.5 mr-2 rounded text-sm font-medium bg-white/30 dark:bg-gray-100/20 border border-blue-200 shadow-sm hover:bg-white/50 hover:border-blue-300 transition"
                  >
                    <span className="text-yellow-300">+</span>{' '}
                    <span className="text-white">Novo Prazo</span>
                  </button>
                  {([
                    ['producao', '📋 Produção'],
                    ['pronto_protocolo', `📥 Prontos para Protocolo${nPP ? ` (${nPP})` : ''}`],
                    ['concluidos', `✅ Concluídos${nCC ? ` (${nCC})` : ''}`]
                  ] as const).map(
                    ([k, label]) => (
                      <button
                        key={k}
                        onClick={() => setAba(k)}
                        className={`px-3 py-1.5 rounded text-sm transition ${
                          aba === k
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {label}
                      </button>
                    ),
                  )}
                </div>
              )}

              {aba === 'producao' ? (
                <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 items-start">
                  {colNF && (
                    <ColunaKanban
                      coluna={colNF} cor="vermelho" hint="" diasSemana={dias}
                      onCardClick={abrirDetalhe}
                      dropOk={compatColuna('nao_feitos') === 'ok'}
                      dropBloqueado={compatColuna('nao_feitos') === 'bloqueado'}
                    />
                  )}
                  {colEA && (
                    <div className="xl:col-span-4">
                      <EsteiraAndamento
                        coluna={colEA}
                        onCardClick={abrirDetalhe}
                        dropOk={compatColuna('em_andamento') === 'ok'}
                        dropBloqueado={compatColuna('em_andamento') === 'bloqueado'}
                        draggedCard={dragCard}
                      />
                    </div>
                  )}
                </div>
              ) : aba === 'pronto_protocolo' ? (
                <div className="max-w-3xl mx-auto">
                  {colPP && (
                    <ColunaKanban
                      coluna={colPP} cor="verde" hint="aguarda Maria" diasSemana={dias}
                      onCardClick={abrirDetalhe} destacar
                      dropOk={compatColuna('pronto_protocolo') === 'ok'}
                      dropBloqueado={compatColuna('pronto_protocolo') === 'bloqueado'}
                    />
                  )}
                </div>
              ) : (
                <div className="max-w-3xl mx-auto">
                  {colCC && (
                    <ColunaKanban
                      coluna={colCC} cor="cinza" hint="⚪ arquivo" diasSemana={dias}
                      onCardClick={abrirDetalhe}
                      dropOk={compatColuna('concluidos') === 'ok'}
                      dropBloqueado={compatColuna('concluidos') === 'bloqueado'}
                    />
                  )}
                </div>
              )}
            </>
          );
        })()}

      <AnimatePresence>
        {detalheId !== null && (
          <ModalDetalhe idPrazo={detalheId} onClose={() => setDetalheId(null)} />
        )}
      </AnimatePresence>

      {criarOpen && <CriarPrazoDialog onClose={() => setCriarOpen(false)} />}

      {/* Toast de erro do drop (autodissolve em 4-5s) */}
      {erroDrop && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white text-sm px-4 py-2 rounded shadow-lg max-w-md text-center">
          ⚠ {erroDrop}
        </div>
      )}
    </PageShell>

    {/* Preview flutuante do card enquanto arrasta (renderiza fora do flow normal) */}
    <DragOverlay dropAnimation={{ duration: 200 }}>
      {dragCard ? (
        <div className="rotate-2 shadow-xl">
          <CardPrazo card={dragCard} draggable={false} />
        </div>
      ) : null}
    </DragOverlay>
    </DndContext>
  );
}
